import { useEffect, useRef, useSyncExternalStore } from 'react'

import type { AlertTone } from '../ui/Alert'

/**
 * LE REGISTRE DES ÉTATS PERSISTANTS (LOT-H9, recette DA §4.6).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * UN ÉTAT QUI DURE NE SE NOTIFIE PAS PAR UNE BULLE QUI PASSE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * > « Un état persistant ne se notifie pas par une bulle éphémère qui recouvre
 * >   l'interface : c'est un bandeau dans une zone réservée, refermable, qui
 * >   réapparaît tant que la condition dure. » — recette DA §4.6
 *
 * `/monitoring` publiait ses seuils franchis en `toast.error(…, { duration:
 * 10000 })`. Deux conséquences, toutes deux constatées en recette (DA-002) :
 * la bulle recouvrait le cluster d'actions du `PageHeader` — filtre de campagne
 * et deux boutons devenus INATTEIGNABLES — et, dix secondes plus tard, elle
 * disparaissait alors que la condition, elle, durait toujours.
 *
 * Ce registre porte l'autre moitié du contrat : une page DÉCLARE l'ensemble des
 * conditions actuellement vraies, la zone de notification de l'`AppShell` les
 * rend en bandeaux, dans le flux, sous l'en-tête. Rien n'est superposé, donc
 * rien n'est recouvert.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI UN REGISTRE HORS DE REACT, ET PAS UN CONTEXTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'émetteur (une page, DANS `<main>`) est un descendant du récepteur (la zone,
 * AU-DESSUS de `<main>`). Un contexte impose alors un provider qui enveloppe les
 * deux, donc une modification de l'ordre de montage de l'`AppShell`. Un registre
 * de module lu par `useSyncExternalStore` n'impose rien : la zone s'y abonne, la
 * page y publie, aucun des deux ne sait où vit l'autre.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * « REFERMABLE » ET « RÉAPPARAÎT » NE SE CONTREDISENT PAS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La fermeture est mémorisée par `(id, signature)`. La `signature` est
 * l'empreinte de la CONDITION, pas du texte d'habillage : tant que le solde
 * reste sous le même seuil, le bandeau fermé reste fermé ; qu'il repasse un
 * palier et le bandeau revient de lui-même. Quitter la page efface la fermeture
 * avec la portée — le bandeau réapparaît à la visite suivante si la condition
 * dure encore. C'est exactement le comportement demandé.
 */

export interface PersistentNotice {
  /** Identité STABLE de la condition (`monitoring:low-balance`), pas du message. */
  id: string
  tone: AlertTone
  title: string
  description?: string
  /** UNE action au plus : ce qu'il faut faire, pas où regarder. */
  action?: { label: string; onClick: () => void }
  /**
   * Empreinte de la condition. Deux valeurs différentes = deux situations
   * différentes, donc une fermeture réarmée. Y mettre le seuil franchi et le
   * palier, jamais un horodatage — sinon le bandeau se rouvre à chaque refetch.
   */
  signature: string
}

type Listener = () => void

const listeners = new Set<Listener>()

/** Portée → avis déclarés. Une portée = un écran (ou un composant) émetteur. */
const scopes = new Map<string, PersistentNotice[]>()

/** `id` → signature fermée par l'utilisateur. */
const dismissedSignatures = new Map<string, string>()

/**
 * Instantané mémoïsé. `useSyncExternalStore` compare les références : recalculer
 * un tableau neuf à chaque lecture provoquerait une boucle de rendu infinie.
 */
let snapshot: PersistentNotice[] = []

function recompute() {
  const next: PersistentNotice[] = []
  for (const notices of scopes.values()) {
    for (const notice of notices) {
      if (dismissedSignatures.get(notice.id) === notice.signature) continue
      next.push(notice)
    }
  }
  snapshot = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Publie l'ensemble EXACT des conditions vraies pour une portée. */
export function publishNotices(scope: string, notices: PersistentNotice[]) {
  if (notices.length === 0) {
    scopes.delete(scope)
  } else {
    scopes.set(scope, notices)
  }
  recompute()
}

/**
 * Retire une portée ET les fermetures de ses avis : l'écran est quitté, la
 * prochaine visite doit repartir d'un bandeau ouvert si la condition dure.
 */
export function clearScope(scope: string) {
  const notices = scopes.get(scope)
  if (notices) notices.forEach((notice) => dismissedSignatures.delete(notice.id))
  scopes.delete(scope)
  recompute()
}

export function dismissNotice(id: string) {
  for (const notices of scopes.values()) {
    const notice = notices.find((candidate) => candidate.id === id)
    if (notice) {
      dismissedSignatures.set(id, notice.signature)
      break
    }
  }
  recompute()
}

/** Remise à zéro complète — réservée aux tests. */
export function resetNotices() {
  scopes.clear()
  dismissedSignatures.clear()
  recompute()
}

export function useVisibleNotices(): PersistentNotice[] {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot)
}

/**
 * Déclare, depuis un écran, l'ensemble des conditions actuellement vraies.
 *
 * L'effet ne se rejoue que quand le FOND change (identité, ton, signature,
 * textes) : les `onClick` sont lus au clic à travers une ref, donc une closure
 * recréée à chaque rendu ne republie rien — sans quoi la zone se réécrirait à
 * chaque frappe de l'utilisateur sur l'écran émetteur.
 */
export function usePersistentNotices(scope: string, notices: PersistentNotice[]) {
  const latest = useRef(notices)
  latest.current = notices

  const key = notices
    .map((n) => `${n.id}|${n.tone}|${n.signature}|${n.title}|${n.description ?? ''}|${n.action?.label ?? ''}`)
    .join('§')

  useEffect(() => {
    publishNotices(
      scope,
      latest.current.map((notice) => ({
        ...notice,
        action: notice.action
          ? {
              label: notice.action.label,
              // Lecture différée : on rejoue l'action de la version COURANTE.
              onClick: () =>
                latest.current.find((candidate) => candidate.id === notice.id)?.action?.onClick(),
            }
          : undefined,
      })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, key])

  useEffect(() => () => clearScope(scope), [scope])
}

// ══════════════════════════════════════════════════════════════════════════
// PRÉSENCE DE LA ZONE
// ══════════════════════════════════════════════════════════════════════════
//
// La pile de notifications éphémères est montée à la RACINE de l'application
// (`main.tsx`), donc au-dessus du routeur : elle doit fonctionner sur `/login`
// et `/register`, qui n'ont pas d'`AppShell`. Quand l'`AppShell` est là, sa zone
// dans le flux prend le relais — et la pile flottante s'efface, sinon les mêmes
// toasts seraient rendus deux fois (`useToaster` lit un registre global).

let regionMounted = false
const regionListeners = new Set<Listener>()

function subscribeRegion(listener: Listener) {
  regionListeners.add(listener)
  return () => {
    regionListeners.delete(listener)
  }
}

function setRegionMounted(value: boolean) {
  if (regionMounted === value) return
  regionMounted = value
  regionListeners.forEach((listener) => listener())
}

export function useRegionMounted(): boolean {
  return useSyncExternalStore(
    subscribeRegion,
    () => regionMounted,
    () => false,
  )
}

/** Signale, le temps de sa vie, qu'une zone dans le flux rend les notifications. */
export function useRegisterRegion() {
  useEffect(() => {
    setRegionMounted(true)
    return () => setRegionMounted(false)
  }, [])
}

/** Réservé aux tests : remet la présence de zone à zéro. */
export function resetRegion() {
  setRegionMounted(false)
}
